import qs from 'qs';
import { AxiosResponse } from 'axios';
import {
  SeriesNumber,
  SinisterColorsEnum,
  SinisterDebitNote,
  SinisterDebitNoteConfiguration,
  SinisterDocuments,
  SinistersDashboard,
  SinistersInsurers,
  SinistersIssuers,
  SinistersLog,
  SinisterStatusEnum,
  SinistersType,
  SinistersView,
} from '~/types/sinisters';
import apiSinister from '~/utils/apiSinister';
import { Pagination, SelectItem } from '~/types';
import { PAGE_SIZE } from '~/constants';
import { formatInTimeZone } from '~/utils/formatToTimeZone';
import { formatCurrency } from '~/utils/formatCurrency';
import { QueryFilterContext } from '~/hooks/useFilter/types';

export async function getSinistersTypes(): Promise<SinistersType[]> {
  const { data } = await apiSinister.get<SinistersType[]>('/tipos-sinistro/');
  return data;
}

export async function getSinistersTypeAsSelect(): Promise<SelectItem[]> {
  const { data } = await apiSinister.get<SinistersType[]>('/tipos-sinistro/');

  const types = data.map(type => ({
    value: String(type.id),
    label: type.nome,
  }));

  return types;
}

export async function getSinistersList(
  page: number,
  search: string,
  status: string,
  orderby: string,
  query?: any,
  pageSize = PAGE_SIZE,
): Promise<Pagination<SinistersView>> {
  const { data } = await apiSinister.get<Pagination<SinistersView>>(
    '/sinistros',
    {
      params: {
        page,
        size: pageSize,
        ordering: orderby,
        ...(search ? { search } : {}),
        ...(status ? { status } : {}),
        ...{ ...query },
      },
      paramsSerializer: params => {
        return qs.stringify(params, { arrayFormat: 'repeat' });
      },
    },
  );

  const sinister = data.results.map(s => ({
    ...s,
    formattedStatus: SinisterStatusEnum[s.status],
    colorScheme: SinisterColorsEnum[s.status],
    formattedDate: formatInTimeZone(`${s.criado_em}`, 'dd/MM/yyyy'),
  }));

  return {
    ...data,
    results: sinister,
  };
}

export async function getSeriesNumber(
  search: string,
  query?: any,
  group?: string,
): Promise<Pagination<SeriesNumber>> {
  const { data } = await apiSinister.get<Pagination<SeriesNumber>>('/series/', {
    params: {
      page: 1,
      size: PAGE_SIZE,
      group,
      ...(search ? { search } : {}),
      ...{ ...query },
    },
    paramsSerializer: params => {
      return qs.stringify(params, { arrayFormat: 'repeat' });
    },
  });

  const series = data.results.map(s => ({
    ...s,
  }));

  return {
    ...data,
    results: series,
  };
}

export async function getSinisterById(id?: number): Promise<SinistersView> {
  const { data } = await apiSinister.get<SinistersView>(`/sinistros/${id}`);

  const sinister = data;
  sinister.formattedStatus = SinisterStatusEnum[sinister.status];
  sinister.colorScheme = SinisterColorsEnum[sinister.status];
  sinister.formattedDate = sinister?.criado_em
    ? formatInTimeZone(`${sinister.criado_em}`, 'dd/MM/yyyy')
    : '';
  sinister.formattedValorNotaDebito = formatCurrency(
    Number(sinister?.nota_de_debito_valor),
  );

  return sinister;
}

export async function getSinisterPdf(
  id: string,
  url: string,
): Promise<AxiosResponse> {
  return apiSinister.get(`/sinistros/${id}/download/`, {
    params: {
      documento: url,
    },
    headers: {
      'Content-Type': 'application/pdf',
    },
    responseType: 'blob' as 'json',
  });
}

export async function getInsuranceLetter(
  sinisterId: number,
  insurerId: number,
  policyNumber: string,
): Promise<AxiosResponse<any>> {
  return apiSinister.post(
    `/registrar-apolice/${sinisterId}/gerar-carta/`,
    { numero_apolice: policyNumber, seguradora: insurerId },
    {
      headers: {
        'Content-Type': 'application/pdf',
      },
      responseType: 'blob' as 'json',
    },
  );
}

export async function registerInsuranceLetter(
  sinisterId: number,
  insurerId: number,
  policyNumber: string,
): Promise<AxiosResponse<any>> {
  return apiSinister.put(`/registrar-apolice/${sinisterId}/`, {
    numero_apolice: policyNumber,
    seguradora: insurerId,
  });
}

export async function getSinisterLogById(id?: number): Promise<SinistersLog[]> {
  const { data } = await apiSinister.get<SinistersLog[]>(
    `/log-sinistro/${id}/`,
  );

  const log = data.map(s => ({
    ...s,
    formattedDate: formatInTimeZone(
      `${s.data_evento}`,
      'dd/MM/yyyy HH:mm',
      'America/Sao_Paulo',
    ),
  }));

  return log;
}

export async function getSinisterDashboard(
  query?: any,
): Promise<SinistersDashboard> {
  const { data } = await apiSinister.get<SinistersDashboard>(
    `/sinistros/dashboard/`,
    {
      params: {
        ...{ ...query },
      },
      paramsSerializer: params => {
        return qs.stringify(params, { arrayFormat: 'repeat' });
      },
    },
  );

  return data;
}

export async function getSinistersInsurers(): Promise<SinistersInsurers[]> {
  const { data } = await apiSinister.get<SinistersInsurers[]>('/seguradora/');
  return data;
}

export async function getSinistersInsurersAsSelect(): Promise<SelectItem[]> {
  const { data } = await apiSinister.get<SinistersType[]>('/seguradora/');

  const insurers = data.map(insurer => ({
    value: String(insurer.id),
    label: insurer.nome,
  }));

  return insurers;
}

export async function getSinistersIssuer(): Promise<SinistersIssuers[]> {
  const { data } = await apiSinister.get<SinistersIssuers[]>('/emitente/');
  return data;
}

export async function getSinistersIssuersAsSelect(): Promise<SelectItem[]> {
  const { data } = await apiSinister.get<SinistersIssuers[]>('/emitente/');

  const issuers = data.map(issuer => ({
    value: String(issuer.id),
    label: issuer.nome,
  }));

  return issuers;
}

export async function getDebitNoteConfiguration(): Promise<SinisterDebitNoteConfiguration> {
  const { data } = await apiSinister.get<SinisterDebitNoteConfiguration[]>(
    `/configuracao/nota-debito/`,
  );
  return data[0];
}

export async function getDebitNote(
  sinisterId: number,
  notaDebito: SinisterDebitNote,
): Promise<AxiosResponse<any>> {
  return apiSinister.post(
    `/enviar-nota-debito/${sinisterId}/gerar-nota-debito/`,
    {
      referente: notaDebito.referente,
      data_de_vencimento: notaDebito.data_de_vencimento,
      id_emitente: notaDebito.id_emitente,
      valor: notaDebito.valor,
      numero_nota_debito: notaDebito.numero_nota_debito,
    },
    {
      headers: {
        'Content-Type': 'application/pdf',
      },
      responseType: 'blob' as 'json',
    },
  );
}

export async function getSinisterDocumentsById(
  id?: number,
): Promise<SinisterDocuments[]> {
  const { data } = await apiSinister.get<SinisterDocuments[]>(
    `/anexar-documento/${id}`,
  );

  return data.map((value, index) => {
    return {
      ...value,
      id_check: index + 1,
      data_upload: value.data_upload
        ? formatInTimeZone(value.data_upload, 'dd/MM/yyyy')
        : value.data_upload,
    };
  });
}

export async function getSinisterDocument(
  sinisterId: string,
  id: number,
  tipo: string,
): Promise<AxiosResponse> {
  return apiSinister.get(`/anexar-documento/${sinisterId}/download/`, {
    params: {
      id,
      documento: tipo,
    },
    headers: {
      'Content-Type': 'application/pdf',
    },
    responseType: 'blob' as 'json',
  });
}

export async function getSinisterDocumentsZip(
  sinisterId: string,
  query_id: number[],
  query_tipo: string[],
): Promise<AxiosResponse> {
  return apiSinister.get(`/anexar-documento/${sinisterId}/download-zip/`, {
    params: {
      ids: query_id,
      documentos: query_tipo,
    },
    paramsSerializer: params => {
      return qs.stringify(params, { arrayFormat: 'repeat' });
    },
    headers: {
      'Content-Type': 'application/zip',
    },
    responseType: 'blob',
  });
}

export async function getSinistersXlsx(
  search?: string,
  status?: string,
  orderby?: string,
  query?: QueryFilterContext,
): Promise<AxiosResponse<any>> {
  const url = '/sinistros/xlsx';
  const groups = query?.groups?.map(e => e.value);
  const headoffices = query?.headOffices?.map(e => e.value);
  const branches = query?.branches?.map(e => e.value);
  return apiSinister.get(url, {
    params: {
      ...(search && { search }),
      ...(status && { status }),
      ordering: orderby,
      ...(groups && { groups }),
      ...(headoffices && { headoffices }),
      ...(branches && { branches }),
    },
    paramsSerializer: params => {
      return qs.stringify(params, { arrayFormat: 'repeat' });
    },
    headers: {
      'Content-Type': 'application/xlsx',
    },
    responseType: 'blob' as 'json',
  });
}
